// SplitButton: primary action + dropdown chevron con menu di opzioni.
// Se options è assente/vuoto, degrada a bottone semplice (no chevron, no menu).
// Click outside o Escape chiudono il menu. Stato del menu in useState.

import { useEffect, useRef, useState } from 'react';

export interface SplitButtonOption {
  label: string;
  onClick: () => void;
}

export interface SplitButtonProps {
  label: string;
  onPrimaryClick: () => void;
  options?: SplitButtonOption[];
  busy?: boolean;
  disabled?: boolean;
}

export function SplitButton({ label, onPrimaryClick, options, busy, disabled }: SplitButtonProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMenu = !!options && options.length > 0;
  const isDisabled = disabled || busy;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const containerClass = hasMenu ? 'audiq-splitbtn' : 'audiq-splitbtn audiq-splitbtn--solo';

  return (
    <div className={containerClass} ref={containerRef}>
      <button
        type="button"
        className="audiq-splitbtn__primary"
        onClick={onPrimaryClick}
        disabled={isDisabled}
      >
        {label}
      </button>
      {hasMenu ? (
        <button
          type="button"
          className="audiq-splitbtn__chevron"
          onClick={() => setOpen((v) => !v)}
          disabled={isDisabled}
          aria-label="Altre opzioni"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          ▾
        </button>
      ) : null}
      {open && hasMenu ? (
        <div className="audiq-splitbtn__menu" role="menu">
          {options.map((opt) => (
            <button
              key={opt.label}
              type="button"
              role="menuitem"
              className="audiq-splitbtn__menu-item"
              onClick={() => {
                setOpen(false);
                opt.onClick();
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
